import * as R from 'ramda';

import { assetMarkers } from '@atom/constants/markerIconConstants';
import { Icon } from '@atom/mui';
import { SchemaTree, SchemaTreeAttribute } from '@atom/types/schema';
// TODO(AM-18745): remove toggle
import { showDevelopmentFeature } from '@atom/utilities/featureToggleUtilities';
import { getSchemaIconsList } from '@atom/utilities/markerUtilities';

import { colors } from './colorUtilities';

const NO_ASSET_TYPE_LEAF_NODE_COLOR = '#999999';
const DEFAULT_ASSET_SCHEMA_COLOR_ID = 0;
const DEFAULT_ASSET_SCHEMA_MARKER_ID = 8;

const getSchemaColorFromColorId = schemaColorId => {
  if (schemaColorId === 'DEFAULT') {
    return NO_ASSET_TYPE_LEAF_NODE_COLOR;
  }

  return colors[schemaColorId] || colors[DEFAULT_ASSET_SCHEMA_COLOR_ID];
};

const schemaIconsList = getSchemaIconsList();

export const getSchemaIconFromMarkerId = markerId => {
  return assetMarkers[markerId] || assetMarkers[DEFAULT_ASSET_SCHEMA_MARKER_ID];
};

export const getSchemaIconFromSchemaOrAsset = (
  schema,
  params = {
    imgOffset: '0',
    iconSize: '34px',
    marginTop: '3px',
  },
) => {
  if (!schema) {
    return <div />;
  }

  const { imgOffset, iconSize, marginTop } = params;
  const { isMaterial, locationType, colorId: baseColorId, markerId } = schema;

  // If colorId is not set, markerId should be used to determine the color
  const colorId = baseColorId || markerId;
  const svgIcon =
    assetMarkers[markerId] || assetMarkers[DEFAULT_ASSET_SCHEMA_MARKER_ID];

  const schemaColor = colors[colorId] || 0;

  const iconStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: schemaColor,
    width: iconSize,
    height: iconSize,
  };

  const imgStyle = {
    marginTop,
    marginLeft: imgOffset,
    marginRight: imgOffset,
  };

  if (showDevelopmentFeature) {
    return !R.isNil(locationType) ? (
      <img style={imgStyle} src={svgIcon} />
    ) : (
      <Icon style={iconStyle}>category</Icon>
    );
  }

  return isMaterial ? (
    <Icon style={iconStyle}>category</Icon>
  ) : (
    <img style={imgStyle} src={svgIcon} />
  );
};

// returns map of attribute id to attribute for given schema
export const getSchemaAttributesMap = (
  tree: SchemaTree | null | undefined,
): Record<string, SchemaTreeAttribute> =>
  Object.fromEntries(
    (tree?.attributeGroups ?? []).flatMap(group =>
      group.attributes.map(attr => [attr.id, attr]),
    ),
  );

// creates a flat map of schema id to schema tree for the given schema tree, recursively
export const getSchemaTreeMap = (
  tree: SchemaTree | null | undefined,
): Map<string, SchemaTree> => {
  const map = new Map<string, SchemaTree>();

  const walk = (node: SchemaTree | null | undefined) => {
    if (!node || map.has(node.id)) {
      return;
    }

    map.set(node.id, node);

    (node.elements ?? []).forEach(walk);

    (node.elementGroups ?? []).forEach(group =>
      (group.elements ?? []).forEach(walk),
    );
  };

  walk(tree);

  return map;
};

const schemaUtilities = {
  DEFAULT_ASSET_SCHEMA_COLOR_ID,
  getSchemaColorFromColorId,
  getSchemaIconFromMarkerId,
  getSchemaIconFromSchemaOrAsset,
  schemaIconsList,
};

export default schemaUtilities;
